import { getAccountAdvancedOptions, getAccountOverview, getGeneralInfo } from '@api/store/account/actions';
import { getDashboardSettingsStore } from '@api/store/dashboardSettings/actions';
import { AccountDashboardFlow, ProductType } from '@dev/ee-api/ee-api-models';
import { dayjs } from '@dev/ee-tools';
import { identity, isEmpty } from 'lodash-es';
import { useEffect } from 'react';
import { hotjar } from 'react-hotjar';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useActivePlan } from './useActivePlan';
import { useCurrentPlanStatus } from './useCurrentPlanStatus';

export enum HotJarEvents {
    SurveyPaid = 'SurveyPaid',
    PaidUsersMarketing = 'PaidusersMarketing',
    PaidUsersAPI = 'PaidusersApi',
    FreeUsersMarketing = 'FreeusersMarketing',
    FreeUsersAPI = 'FreeusersAPI',
    FreeUsersAPIZeroSent = 'FreeusersAPIZeroSent',
    FreeUsersMarketingZeroSent = 'FreeusersMarketingZeroSent',
    PublishForm = 'PublishForm',
    PublishLandingPage = 'PublishLandingPage',
    NPSEmailAPI = 'NPSEmailAPI',
    NPSEmailMarketing = 'NPSEmailMarketing',
}

export const useHotJar = () => {
    const {
        UserName,
        PublicAccountID,
        // EnablePrivateBranding,
        IsSub,
        StatusFormatted,
        DateCreated,
        ProductType: accProductType,
    } = useSelector(getGeneralInfo);
    const { IsOwnedByReseller } = useSelector(getAccountAdvancedOptions);
    const { TotalEmailsSent } = useSelector(getAccountOverview);
    const { dashboardSettingsList } = useSelector(getDashboardSettingsStore);

    const { ActivePlanDetails } = useActivePlan();
    const { isTrialOrFree, isActive, isExpired } = useCurrentPlanStatus();
    const location = useLocation();

    const surveyConstrains = {
        [HotJarEvents.SurveyPaid]: Object.values({
            isNotSub: !IsSub,
            isPaying: !isTrialOrFree,
            isNotExpired: !isExpired,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
        }).every(identity),
        [HotJarEvents.PaidUsersMarketing]: Object.values({
            isPaying: !isTrialOrFree,
            isMarketing: accProductType === ProductType.Marketing,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            notCB: !IsOwnedByReseller,
        }).every(identity),
        [HotJarEvents.PaidUsersAPI]: Object.values({
            isPaying: !isTrialOrFree,
            isApi: accProductType === ProductType.API,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            notCB: !IsOwnedByReseller,
        }).every(identity),
        [HotJarEvents.FreeUsersMarketing]: Object.values({
            isFreeTier: isTrialOrFree,
            isMarketing: accProductType === ProductType.Marketing,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            notCB: !IsOwnedByReseller,
            hasSentAnything: !!TotalEmailsSent,
        }).every(identity),
        [HotJarEvents.FreeUsersMarketingZeroSent]: Object.values({
            isFreeTier: isTrialOrFree,
            isMarketing: accProductType === ProductType.Marketing,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            notCB: !IsOwnedByReseller,
            hasSentAnything: !TotalEmailsSent,
        }).every(identity),
        [HotJarEvents.FreeUsersAPI]: Object.values({
            isFreeTier: isTrialOrFree,
            isApi: accProductType === ProductType.API,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            notCB: !IsOwnedByReseller,
            hasSentAnything: !!TotalEmailsSent,
        }).every(identity),
        [HotJarEvents.FreeUsersAPIZeroSent]: Object.values({
            isFreeTier: isTrialOrFree,
            isApi: accProductType === ProductType.API,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            notCB: !IsOwnedByReseller,
            hasSentAnything: !TotalEmailsSent,
        }).every(identity),
        [HotJarEvents.NPSEmailAPI]: Object.values({
            isPaying: !isTrialOrFree,
            min30DaysFromCreatingAccount: dayjs().diff(dayjs(DateCreated).utc(true), 'day') > 30,
            // isMarketing: accProductType === ProductType.API,
            isNotUnderReview: !StatusFormatted?.includes('UnderReview'),
            // it is based on this flow as it required for the account to be active, and has proper rank
            rankActiveTrustedOrPreferred: dashboardSettingsList?.DashboardFlows?.includes(AccountDashboardFlow.ExtendedPaymentRemovalFlow),
            notCB: !IsOwnedByReseller,
        }).every(identity),
    };
    const showSurvey = (surveyTrigger: HotJarEvents) => {
        if (isEmpty(ActivePlanDetails)) return; // it means that the product is not yet laoded
        if (!surveyConstrains?.[surveyTrigger]) return;
        if (!hotjar.initialized()) return;
        hotjar.event(surveyTrigger);
    };

    const defaultAttributes = {
        // EnabledCustomBranding: EnablePrivateBranding,
        AccID: PublicAccountID,
        PlanCode: ActivePlanDetails?.PlanCode,
        IsPaid: !isTrialOrFree,
        IsActive: isActive,
        IsExpired: isExpired,
        IsUnderReview: StatusFormatted?.includes?.('UnderReview'),
        IsMain: !IsSub,
    };

    // const surveryAttributes = {
    //     [HotJarEvents.PaidUsersMarketing]: {
    //         // EnabledCustomBranding: EnablePrivateBranding,
    //         AccID: PublicAccountID,
    //         PlanCode: ActivePlanDetails?.PlanCode,
    //         IsPaid: !isTrialOrFree,
    //         IsActive: isActive,
    //         IsExpired: isExpired,
    //         IsUnderReview: StatusFormatted?.includes?.('UnderReview'),
    //         IsMain: !IsSub,
    //     },
    //     [HotJarEvents.SurveyPaid]: {
    //         EnabledCustomBranding: EnablePrivateBranding,
    //         AccID: PublicAccountID,
    //         PlanCode: ActivePlanDetails?.PlanCode,
    //         IsPaid: !isTrialOrFree && isActive && !isExpired,
    //         IsNotUnderReview: !StatusFormatted?.includes?.('UnderReview'),
    //         IsMain: !IsSub,
    //     },
    // };

    const sendUserAttributes = () => {
        hotjar.identify(PublicAccountID, defaultAttributes);
    };

    useEffect(() => {
        // !hotjar.initialized() && hotjar.initialize(184870, 7);
        if (!hotjar.initialized()) return;
        sendUserAttributes();
    }, [UserName, isTrialOrFree, ActivePlanDetails?.BillingPlanID, location.pathname, location.search]);

    return {
        showSurvey,
    };
};
